import type { JSX } from "react";
import {
  IconRoundBadge,
  TopBarHeader,
  IconButton,
  Icon,
} from "../../components/ui";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { DetailPane } from "../../components/layout/DetailPane";
import { AgentPanel } from "../../agent/AgentPanel";
import { useEpisodeDetailQuery } from "./queries";
import { getSourceVisual } from "./helpers";
import { schemaIdToModuleId } from "../../agent/schemaIdToModuleId";
import type { DetailPanelProps } from "../_base/types";

export function EpisodeDetailWrapper({
  entityId,
}: DetailPanelProps): JSX.Element {
  // entityId comes as raw UUID from the list (mapListItem returns raw id)
  const uuid = entityId.startsWith("agent:") ? entityId.slice(6) : entityId;
  const { data: episode } = useEpisodeDetailQuery(uuid);

  if (!episode) {
    return (
      <DetailPane>
        <div className="flex items-center justify-center h-full text-content-tertiary text-base">
          No conversations
        </div>
      </DetailPane>
    );
  }

  // Plan #8: derive source module / icon from the started_with link target's schema_id
  // instead of the removed context_key.
  const startedWith = episode.linked_entities.find((e) => e.link_kind === "started_with");
  const sourceModuleId = schemaIdToModuleId(startedWith?.schema_id);
  const visuals = getSourceVisual(sourceModuleId);

  return (
    <DetailPane
      headerNode={
        <TopBarHeader
          leading={
            <IconRoundBadge
              iconName={visuals.icon}
              colorClassName={`bg-${visuals.color}`}
              iconSize={14}
              className="h-8 w-8"
            />
          }
          title={episode.title}
          subtitle={
            <span className="flex items-center gap-1.5">
              <StatusBadge status={episode.status} />
              {sourceModuleId === "triggers" && (
                <span className="text-[10px] rounded-full px-1.5 py-0.5 shrink-0 bg-orange-500/15 text-orange-400">
                  Auto
                </span>
              )}
            </span>
          }
          actions={
            <IconButton variant="ghost">
              <Icon name="ellipsis-vertical" size={15} />
            </IconButton>
          }
        />
      }
    >
      <AgentPanel
        moduleName={sourceModuleId}
        conversationKey={episode.id}
        selectedItemId={startedWith?.id ?? uuid}
        selectedChatName={episode.title}
        contextTitle="Chats"
        archived={episode.status === "archived"}
      />
    </DetailPane>
  );
}
